
export type ViewMode = 'projects' | 'developers';
export type TrackingTab = 'entry' | 'analytics';

export interface Toast { id: number; message: string; type: string; }
export interface TimeEntry {
    id: string; date: Date; developerId: number; projectId: number;
    featureId: number | null; hours: number; type: 'planned' | 'support'; description: string;
}
export interface AllocPeriod { weekStart: Date; days: string[]; }

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];

export function projectColor(p: any): string {
    if (p.color) return p.color;
    const name = p.name || '';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

export function userColor(u: any): string {
    if (u.color) return u.color;
    const name = (u.fullName || u.firstName || '') as string;
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

export function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff); d.setHours(0, 0, 0, 0);
    return d;
}

export function addDays(date: Date, days: number): Date {
    const d = new Date(date); d.setDate(d.getDate() + days); return d;
}

export function daysBetween(d1: Date, d2: Date): number {
    const a = new Date(d1); a.setHours(0, 0, 0, 0);
    const b = new Date(d2); b.setHours(0, 0, 0, 0);
    return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatDate(date: Date | string | null): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function isToday(date: Date): boolean {
    return new Date(date).toDateString() === new Date().toDateString();
}

export function isWeekend(date: Date): boolean {
    const d = new Date(date).getDay();
    return d === 0 || d === 6;
}

export function getDayOfWeek(date: Date): string {
    return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][new Date(date).getDay()];
}

/**
 * [T54157] Resolve a ticket's assignee id across ticket kinds.
 * mobx-spine parses a null FK as an empty model instance (object with id null),
 * so `t.assignee?.id ?? t.assignee` would return the empty object — unwrap it.
 */
export function resolveTicketAssigneeId(t: any): number | null {
    const resolve = (v: any): number | null => {
        if (v === null || v === undefined) return null;
        if (typeof v === 'object') return (v.id ?? null) === null ? null : Number(v.id);
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
    };
    const primary = resolve(t.assignee);
    if (primary !== null) return primary;
    return resolve(t.assignedTo);
}

/**
 * [T54157] Normalize a date-ish value (moment from Casts.date, Date, or string)
 * to a plain Date. `new Date(moment)` silently produces an Invalid Date.
 */
export function toDate(v: any): Date | null {
    if (!v) return null;
    if (v.toDate && typeof v.toDate === 'function') return v.toDate();          // moment
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;                  // Date
    const d = new Date(v);                                                        // string/number
    return isNaN(d.getTime()) ? null : d;
}
